import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { formatLocalDate } from "../../utils/formatDate";
import { CustomTable } from "../../componentes/table/CustomTable";

const fmtDate = (v) => formatLocalDate(v);
const fmtNum = (v, d = 2) => v != null ? Number(v).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—";
const fmtInt = (v) => v != null ? Number(v).toLocaleString("pt-BR") : "—";

function StatCard({ icon, label, value, color = "teal" }) {
    const colors = {
        teal: "bg-teal-50   border-teal-200   text-teal-700",
        blue: "bg-blue-50   border-blue-200   text-blue-700",
        violet: "bg-violet-50 border-violet-200 text-violet-700",
        amber: "bg-amber-50  border-amber-200  text-amber-700",
        orange: "bg-orange-50 border-orange-200 text-orange-700",
        green: "bg-green-50  border-green-200  text-green-700",
        rose: "bg-rose-50   border-rose-200   text-rose-700",
        gray: "bg-muted border-border text-foreground",
    };
    return (
        <div className={`rounded-xl border p-3 flex items-center gap-3 ${colors[color]}`}>
            <i className={`${icon} text-lg w-5 text-center flex-shrink-0`} />
            <div className="min-w-0">
                <p className="text-[10px] opacity-70 leading-tight truncate">{label}</p>
                <p className="text-sm font-bold leading-tight truncate">{value}</p>
            </div>
        </div>
    );
}

export default function TabelaEntregaVolume({ data = [], loading = false }) {

    const stats = useMemo(() => {
        if (!data.length) return null;
        const totalClientes = data.reduce((a, r) => a + (r.totalCliente ?? 0), 0);
        const totalCubos = data.reduce((a, r) => a + (Number(r.cubos) || 0), 0);
        const totalCxf = data.reduce((a, r) => a + (Number(r.caixasFisicas) || 0), 0);
        const totalCxu = data.reduce((a, r) => a + (Number(r.caixasUnitarias) || 0), 0);
        const totalPeso = data.reduce((a, r) => a + (Number(r.peso) || 0), 0);
        const totalKmReal = data.reduce((a, r) => a + (r.kmRealizado ?? 0), 0);
        const totalPallet = data.reduce((a, r) => a + (r.quantidadePallet ?? 0), 0);
        return { totalClientes, totalCubos, totalCxf, totalCxu, totalPeso, totalKmReal, totalPallet, total: data.length };
    }, [data]);

    const columns = useMemo(() => [
        { key: "filialNome", label: "Filial", sortable: true },
        {
            key: "escalaVinculada",
            label: "Escala Vinc.",
            sortable: true,
            render: (item) => <span className={`px-1 py-1 font-semibold rounded-lg text-[10px] text-white ${item ? 'bg-success' : 'bg-yellow-500'}`}>{item ? "Vinculada" : "Não Vinc."}</span>
        },

        { key: "carga", label: "Carga", sortable: true, className: "font-semibold" },
        { key: "transporte", label: "Transporte", sortable: true, center: true },
        { key: "veiculoPlaca", label: "Placa", sortable: true, center: true },
        { key: "tipoCarga", label: "Tipo Carga", sortable: true, center: true },
        { key: "canal", label: "Canal", sortable: true, center: true },
        {
            key: "dataEntrega",
            label: "Dt. Entrega",
            sortable: true,
            center: true,
            render: (v) => fmtDate(v),
        },
        {
            key: "dataCriacao",
            label: "Dt. Criação",
            sortable: true,
            center: true,
            render: (v) => fmtDate(v),
        },
        {
            key: "totalCliente",
            label: "Clientes",
            sortable: true,
            center: true,
            className: "bg-primary/5 font-semibold",
            render: (v) => fmtInt(v),
        },
        {
            key: "quantidadePallet",
            label: "Pallets",
            sortable: true,
            center: true,
            className: "bg-primary/5",
            render: (v) => fmtInt(v),
        },
        {
            key: "caixasFisicas",
            label: "Cx. Físicas",
            sortable: true,
            center: true,
            className: "bg-violet-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "dropSizeCxf",
            label: "Drop Cx. Fís.",
            sortable: true,
            center: true,
            className: "bg-violet-50",
            render: (v) => fmtNum(v),
        },
        {
            key: "caixasUnitarias",
            label: "Cx. Unitárias",
            sortable: true,
            center: true,
            className: "bg-violet-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "cubos",
            label: "Cubos",
            sortable: true,
            center: true,
            className: "bg-teal-50 font-semibold",
            render: (v) => fmtNum(v),
        },
        {
            key: "dropSizeCubo",
            label: "Drop Cubo",
            sortable: true,
            center: true,
            className: "bg-teal-50",
            render: (v) => fmtNum(v),
        },
        {
            key: "peso",
            label: "Peso",
            sortable: true,
            center: true,
            className: "bg-amber-50",
            render: (v) => v != null ? `${fmtNum(v)} kg` : "—",
        },
        {
            key: "taxaPeso",
            label: "Taxa Peso",
            sortable: true,
            center: true,
            className: "bg-amber-50",
        },
        {
            key: "capacidadeCubica",
            label: "Cap. Cúbica",
            sortable: true,
            center: true,
            className: "bg-muted",
            render: (v) => fmtNum(v),
        },
        {
            key: "capacidadePeso",
            label: "Cap. Peso",
            sortable: true,
            center: true,
            className: "bg-muted",
            render: (v) => fmtNum(v),
        },
        {
            key: "taxaOcupacao",
            label: "Taxa Ocupação",
            sortable: true,
            center: true,
            className: "bg-green-50 font-semibold",
        },
        {
            key: "kmRealizado",
            label: "KM Real.",
            sortable: true,
            center: true,
            className: "bg-orange-50",
            render: (v) => fmtInt(v),
        },
        {
            key: "kmPlanejado",
            label: "KM Plan.",
            sortable: true,
            center: true,
            className: "bg-orange-50",
            render: (v) => fmtInt(v),
        },
        {
            key: "statusFornecimento",
            label: "Fornecimento",
            sortable: true,
            center: true,
            render: (v) => v
                ? <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted text-muted-foreground">{v}</span>
                : <span className="text-muted-foreground/50">—</span>,
        },
        {
            key: "statusPco",
            label: "PCO",
            sortable: true,
            center: true,
            render: (v) => v
                ? <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/5 text-primary">{v}</span>
                : <span className="text-muted-foreground/50">—</span>,
        },
        {
            key: "duracao",
            label: "Duração",
            sortable: true,
            center: true,
            className: "bg-muted",
        },
        {
            key: "horaInicio",
            label: "Início",
            sortable: true,
            center: true,
            className: "bg-muted",
        },
        {
            key: "horaFinal",
            label: "Final",
            sortable: true,
            center: true,
            className: "bg-muted",
        },
    ], []);

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-background">

            {/* KPIs */}
            {stats && (
                <div className="bg-card border-b border-border px-4 py-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-2">
                        <StatCard icon="fa fa-users" label="Total Clientes" value={fmtInt(stats.totalClientes)} color="violet" />
                        <StatCard icon="fa fa-th" label="Pallets" value={fmtInt(stats.totalPallet)} color="amber" />
                        <StatCard icon="fa fa-cube" label="Cubos" value={fmtNum(stats.totalCubos)} color="teal" />
                        <StatCard icon="fa fa-box" label="Cx. Físicas" value={fmtNum(stats.totalCxf)} color="violet" />
                        <StatCard icon="fa fa-cubes" label="Cx. Unitárias" value={fmtNum(stats.totalCxu)} color="violet" />
                        <StatCard icon="fa fa-balance-scale" label="Peso Total" value={`${fmtNum(stats.totalPeso)} kg`} color="orange" />
                        <StatCard icon="fa fa-road" label="KM Realizado" value={fmtInt(stats.totalKmReal)} color="green" />
                    </div>
                </div>
            )}

            <CustomTable
                data={data}
                columns={columns}
                showHeader={false}
                loading={loading}
                maxHeight="calc(100vh - 250px)"
                exportFilename="Base de Entregas Volumes"
                exportable
                pagination
                pageSize={50}
                className="text-[11px]"
                footer={(rows) => {
                    const totalCubos = rows.reduce((a, r) => a + (Number(r.cubos) || 0), 0);
                    const totalCxf = rows.reduce((a, r) => a + (Number(r.caixasFisicas) || 0), 0);
                    const totalCxu = rows.reduce((a, r) => a + (Number(r.caixasUnitarias) || 0), 0);
                    const totalKm = rows.reduce((a, r) => a + (r.kmRealizado ?? 0), 0);
                    const totalCli = rows.reduce((a, r) => a + (r.totalCliente ?? 0), 0);
                    return (
                        <div className="flex flex-wrap justify-end gap-5 p-2 text-xs font-semibold text-foreground">
                            <span>Registros: {rows.length}</span>
                            <span className="text-violet-700">Clientes: {fmtInt(totalCli)}</span>
                            <span className="text-teal-700">Cubos: {fmtNum(totalCubos)}</span>
                            <span className="text-violet-600">Cx. Fís.: {fmtNum(totalCxf)} · Cx. Unit.: {fmtNum(totalCxu)}</span>
                            <span className="text-orange-600">KM: {fmtInt(totalKm)}</span>
                        </div>
                    );
                }}
            />
        </motion.div>
    );
}
